/**
 * Time-range parameter chip (S10): fixed 7/28/90-day windows plus a custom
 * from/to picker (B2). Values are [start, end) UTC-anchored — the custom
 * end date is INCLUSIVE in the UI ("to June 30") and exclusive on the wire.
 */
import { useState } from 'react';
import { CalendarRange } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { BoundParams, TimeRangeValue } from '../../types/dashboardTypes';
import { ParamChipButton } from './ParamChipButton';

const FIXED_DAYS = [7, 28, 90] as const;

type Props = {
  defaultDays: 7 | 28 | 90;
  value: BoundParams[string] | undefined;
  onChange: (next: number | TimeRangeValue) => void;
};

const summarize = (value: Props['value'], defaultDays: number): string => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return `${value.from} – ${value.to}`;
  }
  return `Last ${typeof value === 'number' ? value : defaultDays} days`;
};

export function TimeRangeChip({ defaultDays, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const custom = value && typeof value === 'object' && !Array.isArray(value) ? (value as TimeRangeValue) : null;
  const [from, setFrom] = useState(custom?.from ?? '');
  const [to, setTo] = useState(custom?.to ?? '');
  const activeDays = custom ? null : typeof value === 'number' ? value : defaultDays;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <ParamChipButton
          icon={<CalendarRange className="h-3 w-3" />}
          label="Range"
          value={summarize(value, defaultDays)}
          testId="param-chip-range"
          // A custom range value ("YYYY-MM-DD – YYYY-MM-DD") is bounded in
          // length — no need for the shared chip's truncating max-width.
          className="max-w-none"
        />
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="flex flex-col gap-3">
          <div className="flex gap-1.5">
            {FIXED_DAYS.map((days) => (
              <Button
                key={days}
                size="sm"
                variant={activeDays === days ? 'default' : 'outline'}
                className="h-7 flex-1 text-xs"
                data-testid={`param-range-days-${days}`}
                onClick={() => {
                  onChange(days);
                  setOpen(false);
                }}
              >
                {days}d
              </Button>
            ))}
          </div>
          <div className="flex flex-col gap-1.5 border-t border-border pt-3">
            <Label className="text-xs text-muted-foreground">Custom range (inclusive)</Label>
            <div className="flex items-center gap-1.5">
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="h-7 text-xs"
                data-testid="param-range-from"
                aria-label="From date"
              />
              <span className="text-xs text-muted-foreground">–</span>
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="h-7 text-xs"
                data-testid="param-range-to"
                aria-label="To date"
              />
            </div>
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={!from || !to || from > to}
              data-testid="param-range-apply"
              onClick={() => {
                onChange({ from, to });
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
